import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { LitElement, css, html, nothing } from "lit";
import { property } from "lit/decorators.js";
import { msg } from "@lit/localize";
//#region src/pd-suggestion-box/PdSuggestionPanel.ts
/**
* Dropdown panel displaying suggestion results.
*
* @tagname pd-suggestion-panel
*
* @property {boolean} loading - Whether data is loading.
* @property {SuggestedElement[]} displayElements - Suggestions to display.
*
* @event element-selected - Fired when a suggestion is clicked.
*
* @cssprop --pd-input-field-width - Panel width. Default: inherits from parent.
* @cssprop --pd-suggestion-panel-bg - Panel background color. Default: `var(--pd-default-bg-col)`.
* @cssprop --pd-suggestion-panel-bg-gradient - Panel gradient background. Default: `linear-gradient(...)`.
* @cssprop --pd-suggestion-panel-border - Panel border. Default: `1px solid var(--pd-default-light-col)`.
* @cssprop --pd-suggestion-panel-shadow - Panel shadow. Default: `var(--pd-shadow-lg)`.
* @cssprop --pd-suggestion-panel-max-height - Panel max height. Default: `15em`.
* @cssprop --pd-suggestion-item-hover-bg - Item hover background. Default: `var(--pd-default-hover-col)`.
* @cssprop --pd-suggestion-item-hover-border - Item hover border. Default: `1px solid var(--pd-default-light-col)`.
* @cssprop --pd-suggestion-item-padding - Item padding. Default: `0.5em`.
* @cssprop --pd-suggestion-item-name-color - Item name color. Default: `var(--pd-default-font-col)`.
* @cssprop --pd-suggestion-item-name-size - Item name font size. Default: `var(--pd-default-font-input-size)`.
* @cssprop --pd-suggestion-item-subinfo-color - Item subinfo color. Default: `var(--pd-default-font-muted-col)`.
* @cssprop --pd-suggestion-item-subinfo-size - Item subinfo font size. Default: `0.875rem`.
* @cssprop --pd-suggestion-loader-color - Loader border color. Default: `var(--pd-default-dark-col)`.
* @cssprop --pd-suggestion-loader-active-color - Loader active color. Default: `var(--pd-default-col)`.
* @cssprop --pd-suggestion-panel-z-index - Panel z-index. Default: `1000`.
* @cssprop --pd-suggestion-item-focus-outline - Focus outline color for keyboard navigation. Default: `var(--pd-default-col, #067394)`.
*/
var PdSuggestionPanel = class extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.loading = false;
		this.displayElements = [];
		this.activeIndex = -1;
	}
	static {
		this.styles = [css`
      :host {
        display: block;
        position: relative;
        z-index: var(--pd-suggestion-panel-z-index, 1000);
      }

      ul {
        position: absolute;
        width: var(--pd-input-field-width);
        list-style: none;
        margin: 0;
        padding: 0;

        background: var(
          --pd-suggestion-panel-bg-gradient,
          linear-gradient(
            to right,
            var(--pd-default-bg-light-col, #f4fcff) 50%,
            var(--pd-suggestion-panel-bg, var(--pd-default-bg-col, white)) 100%
          )
        );

        box-shadow: var(
          --pd-suggestion-panel-shadow,
          var(--pd-shadow-lg, 3px 3px 5px 0 #ccc)
        );
        border: var(
          --pd-suggestion-panel-border,
          1px solid var(--pd-default-light-col, white)
        );
        border-radius: var(--pd-radius-md);

        overflow-y: auto;
        max-height: var(--pd-suggestion-panel-max-height, 15em);
      }

      li {
        margin: 0;
        padding: var(--pd-suggestion-item-padding, 0.5em);
      }

      .select-el:hover,
      .select-el.highlighted {
        background-color: var(
          --pd-suggestion-item-hover-bg,
          var(--pd-default-hover-col)
        );
        border: var(
          --pd-suggestion-item-hover-border,
          1px solid var(--pd-default-light-col, #cadfe5)
        );
        cursor: pointer;
      }

      .select-el.highlighted {
        outline: 2px solid
          var(
            --pd-suggestion-item-focus-outline,
            var(--pd-default-col, #067394)
          );
        outline-offset: -2px;
      }

      .loader-el {
        display: flex;
        align-items: center;
        color: var(
          --pd-suggestion-item-name-color,
          var(--pd-default-font-col, #3c3c3b)
        );
        font-family: var(--pd-default-font-input-family);
        font-size: var(--pd-suggestion-item-subinfo-size, 0.875rem);
      }

      .loader {
        border: 10px solid
          var(--pd-suggestion-loader-color, var(--pd-default-dark-col, #0a3a48));
        border-top: 10px solid
          var(
            --pd-suggestion-loader-active-color,
            var(--pd-default-col, #067394)
          );
        border-radius: 50%;
        width: 10px;
        height: 10px;
        animation: spin 2s linear infinite;
        margin: 0.5em;
      }

      p {
        margin: 0 0 0.25rem;
        font-size: var(
          --pd-suggestion-item-name-size,
          var(--pd-default-font-input-size, 1.05rem)
        );
        font-family: var(--pd-default-font-input-family);
        pointer-events: none;
      }

      small {
        display: block;
        font-size: var(--pd-suggestion-item-subinfo-size, 0.875rem);
        color: var(
          --pd-suggestion-item-subinfo-color,
          var(--pd-default-font-muted-col, #666)
        );
        font-family: var(--pd-default-font-content-family);
      }

      b {
        font-size: var(
          --pd-suggestion-item-name-size,
          var(--pd-default-font-input-size, 1.05rem)
        );
        color: var(
          --pd-suggestion-item-name-color,
          var(--pd-default-font-col, #3c3c3b)
        );
        font-family: var(--pd-default-font-input-family);
      }

      @keyframes spin {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }
    `];
	}
	render() {
		return html`
      ${this.displayElements.length > 0 || this.loading ? html`
              <ul role="listbox">
                ${this.displayElements.map((el, index) => html`
                    <li
                      class="select-el ${index === this.activeIndex ? "highlighted" : ""}"
                      role="option"
                      id="option-${el.id}"
                      aria-selected="${index === this.activeIndex}"
                      @click="${this._selectElement}"
                      data-id="${el.id}"
                    >
                      <p>
                        <b>${el.name}</b> ${el.subInfo ? html`<small>${el.subInfo}</small>` : nothing}
                      </p>
                    </li>
                  `)}
                ${this.loading ? html`
                        <li class="loader-el" aria-live="polite">
                          <div class="loader" aria-hidden="true"></div>
                          ${msg("Daten werden geladen", { id: "pd.suggestion.box.load.data" })}
                        </li>
                      ` : nothing}
              </ul>
            ` : nothing}
    `;
	}
	updated(changedProperties) {
		if (changedProperties.has("activeIndex") && this.activeIndex >= 0) this._scrollActiveIntoView();
	}
	_scrollActiveIntoView() {
		(this.shadowRoot?.querySelector(`#option-${this.displayElements[this.activeIndex]?.id}`))?.scrollIntoView({ block: "nearest" });
	}
	_selectElement(e) {
		const target = e.target;
		const el = this.displayElements.filter((de) => de.id === target?.dataset.id)[0];
		this.dispatchEvent(new CustomEvent("element-selected", {
			detail: el.objectValue || el,
			bubbles: true,
			composed: true
		}));
	}
};
__decorate([property({ type: Boolean })], PdSuggestionPanel.prototype, "loading", void 0);
__decorate([property({ type: Array })], PdSuggestionPanel.prototype, "displayElements", void 0);
__decorate([property({ type: Number })], PdSuggestionPanel.prototype, "activeIndex", void 0);
var tag = "pd-suggestion-panel";
if (!customElements.get(tag)) customElements.define(tag, PdSuggestionPanel);
//#endregion
export { PdSuggestionPanel };
